import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  SyllabusYear,
  Stage,
  Strand,
  Substrand,
  ContentPoint,
  SyllabusContext as SyllabusContextType,
  SyllabusContextState,
  PathwayCode
} from '../types';
import {
  getYears,
  getStrandsByStage,
  getSubstrandsByStrand,
  getContentPointsBySubstrand,
  getContentPointContext,
  searchSyllabus
} from '../services/syllabusService';

const SyllabusContext = createContext<SyllabusContextState | undefined>(undefined);

/**
 * Provider for managing syllabus hierarchy navigation
 * Handles the database-aligned content structure navigation
 */
export const SyllabusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Hierarchy state
  const [years, setYears] = useState<SyllabusYear[]>([]);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [selectedStrand, setSelectedStrand] = useState<Strand | null>(null);
  const [selectedSubstrand, setSelectedSubstrand] = useState<Substrand | null>(null);
  const [selectedContentPoint, setSelectedContentPoint] = useState<ContentPoint | null>(null);
  const [currentContext, setCurrentContext] = useState<SyllabusContextType | null>(null);

  // Stage 5 pathway tracking (null for non-Stage-5 years)
  const [selectedPathway, setSelectedPathway] = useState<PathwayCode | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  /**
   * Loads all available years with their stages
   */
  const loadYears = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      const yearData = await getYears();
      setYears(yearData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load years';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Stage 5 pathway codes that use the cumulative Core-Paths model
   */
  const STAGE5_PATHWAY_CODES: PathwayCode[] = ['5.1', '5.2', '5.3'];

  /**
   * Selects a stage and loads its strands.
   * For Stage 5 pathways, also tracks the selected pathway code.
   */
  const selectStage = useCallback(async (stage: Stage): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      setSelectedStage(stage);
      // Clear downstream selections
      setSelectedStrand(null);
      setSelectedSubstrand(null);
      setSelectedContentPoint(null);
      setCurrentContext(null);

      // Track Stage 5 pathway selection
      if (STAGE5_PATHWAY_CODES.includes(stage.stageCode as PathwayCode)) {
        setSelectedPathway(stage.stageCode as PathwayCode);
      } else {
        setSelectedPathway(null);
      }

      // Load strands for this stage
      const strands = await getStrandsByStage(stage.stageId);
      // Update the stage with loaded strands
      setSelectedStage(prev => prev ? { ...prev, strands } : null);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to select stage';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Selects a year and loads its stages
   * Automatically selects the stage if the year has only one stage (auto-skip)
   * For Year 9/10: shows pathway selector (Standard/Advanced/Extension)
   */
  const selectYear = useCallback(async (year: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      setSelectedYear(year);
      // Clear downstream selections
      setSelectedStage(null);
      setSelectedStrand(null);
      setSelectedSubstrand(null);
      setSelectedContentPoint(null);
      setCurrentContext(null);
      setSelectedPathway(null);

      // Ensure years data is loaded
      let currentYears = years;
      if (years.length === 0) {
        const yearData = await getYears();
        setYears(yearData);
        currentYears = yearData;
      }

      // Find the selected year and check for auto-skip opportunity
      const selectedYearData = currentYears.find(y => y.yearNumber === year);
      if (selectedYearData && selectedYearData.stages.length === 1) {
        // Don't call setIsLoading(false) here - let selectStage handle it
        await selectStage(selectedYearData.stages[0]);
        return; // Exit early, selectStage will handle loading state
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to select year';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [years, selectStage]);

  /**
   * Selects a strand and loads its substrands
   */
  const selectStrand = useCallback(async (strand: Strand): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      setSelectedStrand(strand);
      // Clear downstream selections
      setSelectedSubstrand(null);
      setSelectedContentPoint(null);
      setCurrentContext(null);

      // Load substrands for this strand, filtered by current stage
      const substrands = await getSubstrandsByStrand(strand.strandId, selectedStage?.stageId);
      setSelectedStrand(prev => prev ? { ...prev, substrands } : null);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to select strand';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [selectedStage?.stageId]);

  /**
   * Selects a substrand and loads its content points
   */
  const selectSubstrand = useCallback(async (substrand: Substrand): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      setSelectedSubstrand(substrand);
      // Clear downstream selections
      setSelectedContentPoint(null);
      setCurrentContext(null);

      // Load content points for this substrand
      const contentPointsData = await getContentPointsBySubstrand(substrand.substrandId);
      setSelectedSubstrand(prev => prev ? { ...prev, contentPoints: contentPointsData.data } : null);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to select substrand';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Selects a content point and loads its full context
   */
  const selectContentPoint = useCallback(async (contentPoint: ContentPoint): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      setSelectedContentPoint(contentPoint);

      // Load full context for breadcrumbs
      const context = await getContentPointContext(contentPoint.contentPointId);
      setCurrentContext(context);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to select content point';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Goes back one level in the hierarchy
   * For single-stage years, skips the stage level when going back from strand
   */
  const goBack = async (): Promise<void> => {
    if (selectedContentPoint) {
      setSelectedContentPoint(null);
    } else if (selectedSubstrand) {
      setSelectedSubstrand(null);
    } else if (selectedStrand) {
      setSelectedStrand(null);
    } else if (selectedStage) {
      // Check if current year has only one stage - if so, skip back to year selection
      const currentYear = years.find(y => y.yearNumber === selectedYear);
      if (currentYear && currentYear.stages && currentYear.stages.length === 1) {
        setSelectedYear(null);
        setSelectedStage(null);
      } else {
        setSelectedStage(null);
      }
      setSelectedPathway(null);
    } else if (selectedYear) {
      setSelectedYear(null);
    }
    setCurrentContext(null);
  };

  /**
   * Jumps to a specific level in the hierarchy
   */
  const goToLevel = async (level: 'year' | 'stage' | 'strand' | 'substrand' | 'contentPoint'): Promise<void> => {
    switch (level) {
      case 'year':
        setSelectedStage(null);
        setSelectedStrand(null);
        setSelectedSubstrand(null);
        setSelectedContentPoint(null);
        setSelectedPathway(null);
        break;
      case 'stage':
        setSelectedStrand(null);
        setSelectedSubstrand(null);
        setSelectedContentPoint(null);
        break;
      case 'strand':
        setSelectedSubstrand(null);
        setSelectedContentPoint(null);
        break;
      case 'substrand':
        setSelectedContentPoint(null);
        break;
    }
    setCurrentContext(null);
  };

  /**
   * Resets all navigation state
   */
  const resetNavigation = (): void => {
    setSelectedYear(null);
    setSelectedStage(null);
    setSelectedStrand(null);
    setSelectedSubstrand(null);
    setSelectedContentPoint(null);
    setCurrentContext(null);
    setSelectedPathway(null);
    clearError();
  };

  /**
   * Searches content across the hierarchy
   */
  const searchContent = async (query: string, filters?: any): Promise<any[]> => {
    try {
      setIsLoading(true);
      setError(null);
      return await searchSyllabus(query, filters);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Search failed';
      setError(errorMessage);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Gets content points for a specific substrand
   */
  const getContentPointsForSubstrand = async (substrandId: string): Promise<ContentPoint[]> => {
    try {
      const response = await getContentPointsBySubstrand(substrandId);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch content points:', err);
      return [];
    }
  };

  /**
   * Generates breadcrumb navigation items
   */
  const getBreadcrumbs = (): Array<{ label: string; level: string; onClick: () => void }> => {
    const breadcrumbs: Array<{ label: string; level: string; onClick: () => void }> = [];

    if (selectedYear) {
      breadcrumbs.push({
        label: `Year ${selectedYear}`,
        level: 'year',
        onClick: () => goToLevel('year')
      });
    }

    if (selectedStage) {
      breadcrumbs.push({
        label: selectedStage.stageName || selectedStage.stageCode,
        level: 'stage',
        onClick: () => goToLevel('stage')
      });
    }

    if (selectedStrand) {
      breadcrumbs.push({
        label: selectedStrand.strandName,
        level: 'strand',
        onClick: () => goToLevel('strand')
      });
    }

    if (selectedSubstrand) {
      breadcrumbs.push({
        label: selectedSubstrand.substrandName,
        level: 'substrand',
        onClick: () => goToLevel('substrand')
      });
    }

    if (selectedContentPoint) {
      breadcrumbs.push({
        label: selectedContentPoint.contentDescription.substring(0, 50) + '...',
        level: 'contentPoint',
        onClick: () => { } // Already at this level
      });
    }

    return breadcrumbs;
  };

  return (
    <SyllabusContext.Provider value={{
      // Data
      years,
      selectedYear,
      selectedStage,
      selectedStrand,
      selectedSubstrand,
      selectedContentPoint,
      selectedPathway,
      currentContext,

      // Navigation
      loadYears,
      selectYear,
      selectStage,
      selectStrand,
      selectSubstrand,
      selectContentPoint,
      goBack,
      goToLevel,
      resetNavigation,

      // Content
      searchContent,
      getContentPointsForSubstrand,

      // Utilities
      getBreadcrumbs,

      // State
      isLoading,
      error,
      clearError
    }}>
      {children}
    </SyllabusContext.Provider>
  );
};

/**
 * Hook to use the syllabus context
 */
export const useSyllabus = (): SyllabusContextState => {
  const context = useContext(SyllabusContext);
  if (context === undefined) {
    throw new Error('useSyllabus must be used within a SyllabusProvider');
  }
  return context;
};